import React, { useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { createSelector } from 'reselect';

import Chip from '@material-ui/core/Chip';
import TextField from '@material-ui/core/TextField';
import withWidth, { WithWidth, isWidthDown } from '@material-ui/core/withWidth';
import Autocomplete from '@material-ui/lab/Autocomplete';

import {
  EMPLOYEE_STATUS,
  loadOrgEmployeesByStatus,
} from '../../employees/actions/load-org-employees-by-status';
import { updateEmployee } from '../../employees/actions/update-employee';
import { userSelector } from '../../reducers/current-user-selectors';
import { employeesForOrgIdSelector } from '../../reducers/employee-selectors';
import { addEmployeeGroup } from '../actions/add-employee-group';
import { deleteEmployeeGroup } from '../actions/delete-employee-group';
import { loadEmployeeGroups } from '../actions/load-employee-groups';
import { updateEmployeeGroup } from '../actions/update-employee-group';
import {
  employeeGroupsForOrgIdSelector,
  employeeGroupsForOrgIdStateSelector,
} from '../employee-group-selectors';

import { ProductTelemetryController } from '../../product-telemetry/product-telemetry-controller';
import { UiBreadcrumbHeader } from '../../ui/ui-breadcrumb-header';
import { UiButton } from '../../ui/ui-button';
import { UiDialog } from '../../ui/ui-dialog';
import { employeesRoute } from '../../web-routes';

import { ReactComponent as EmployeeGroupsEmptyState } from './employee-groups-empty-state.svg';

import { employeeName } from '../../employees/utils/employee-name';
import { HelpScout } from '../../util/helpscout-beacon';
import { isIntegrationGroup } from '../../util/is-integration-group';
import { HeaderButtonAddIcon } from '../../util/material-design-icons';
import { EMPLOYEE_PERMISSION } from '../../util/permissions';
import { sortAlphabetically, SORT_ORDER } from '../../util/sort-functions';
import { LoadingMessage } from '../../widgets/loading-message';

const mapStateToProps = (state: any, { orgId }: InputProps) => ({
  user: userSelector(state),
  employees: employeesForOrgIdSelector(state, orgId),
  employeeGroups: employeeGroupsForOrgIdSelector(state, orgId),
  // IF (05/04/20): Checking the state to reload the component when groups are dirty
  employeeGroupsState: employeeGroupsForOrgIdStateSelector(state, orgId),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { orgId }: InputProps,
) => ({
  actions: {
    loadOrgEmployees: () => dispatch(loadOrgEmployeesByStatus(orgId, EMPLOYEE_STATUS.CURRENT)),
    loadOrgEmployeeGroups: () => dispatch(loadEmployeeGroups(orgId)),
    updateOrgEmployeeGroup: (employeeGroupId: number, update: Partial<EmployeeGroup>) =>
      dispatch(updateEmployeeGroup(orgId, employeeGroupId, update)),
    deleteEmployeeGroup: (employeeGroupId: number) =>
      dispatch(deleteEmployeeGroup(orgId, employeeGroupId)),
    addEmployeeGroup: (name: string) => dispatch(addEmployeeGroup(orgId, name)),
    updateEmployee: (employeeId: number, update: Partial<EmployeeUpdateRequest>) =>
      dispatch(updateEmployee(employeeId, update)),
  },
});

const ENTER_KEY_ASCII_VALUE = 13;

const employeesForGroupId = createSelector(
  [
    (employees: Array<Employee>) => employees.filter((e) => e.isCurrentEmployee),
    (_employees: Array<Employee>, groupId: number) => groupId,
  ],
  (employees: Array<Employee>, groupId: number) =>
    employees
      .filter((employee) =>
        employee.EmployeeGroups.find((group) => group.employeeGroupId === groupId),
      )
      .sort(sortAlphabetically((employee) => employeeName(employee).toLowerCase(), SORT_ORDER.ASC)),
);

type InputProps = {
  orgId: number;
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & StateProps & DispatchProps & WithWidth;

export const EmployeeGroupsPage = withWidth()(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(({ orgId, user, employees, employeeGroups, width, actions }: Props) => {
    const [isAddingGroup, setIsAddingGroup] = useState<boolean>();
    const [addingEmployeeToGroupId, setAddingEmployeeToGroupId] = useState<number | null>();
    const [editingGroupId, setEditingGroupId] = useState<number | null>();
    const [newGroupName, setNewGroupName] = useState<string>();
    const [groupNameError, setGroupNameError] = useState<string | null>(null);

    const hasFullEmployeePermission = user.employeesPermission === EMPLOYEE_PERMISSION.ALL;

    useEffect(() => {
      actions.loadOrgEmployeeGroups();
      actions.loadOrgEmployees();
    });

    const handleAddToggle = () => {
      setNewGroupName('');
      setEditingGroupId(null);
      setIsAddingGroup(!isAddingGroup);
      setGroupNameError(null);
    };

    const handleDeleteClick = (groupId: number) => () => {
      // I think we shouldn't be using an alert here - DL
      // eslint-disable-next-line no-alert
      if (window.confirm('Are you sure you want to delete this group?')) {
        actions.deleteEmployeeGroup(groupId);
      }
    };

    const handleRemoveFromGroup = (employee: Employee, employeeGroupId: number) => () => {
      const newGroupIds = employee.EmployeeGroups.map((g) => g.employeeGroupId).filter(
        (id) => id !== employeeGroupId,
      );
      actions.updateEmployee(employee.employeeId, { EmployeeGroups: newGroupIds });

      ProductTelemetryController.logEvent('employee removed from group', {
        value: employee.employeeId,
        metadata: {
          employeeId: String(employee.employeeId),
          employeeGroupId: String(employeeGroupId),
        },
      });
    };

    const handleGroupNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const groupName = e.target.value;
      const error = employeeGroups.find(
        (group) =>
          group.name.toLocaleLowerCase() === groupName.toLocaleLowerCase() &&
          // Allow users to change the capitalization on the group they are editing
          // Even though groups are case insensitive, users may want to adjust formatting
          group.employeeGroupId !== editingGroupId,
      )
        ? 'A group with this name already exists (group names are case insensitive)'
        : null;

      setNewGroupName(groupName);
      setGroupNameError(error);
    };

    const handleKeyDown = (submitFunction: Function) => (e: any) => {
      if (e.keyCode === ENTER_KEY_ASCII_VALUE) {
        submitFunction();
      }
    };

    const handleCreateGroup = async () => {
      if (!newGroupName) return;

      actions.addEmployeeGroup(newGroupName);
      handleAddToggle();
    };

    const handleEditToggle = (group?: EmployeeGroup) => () => {
      setNewGroupName(group ? group.name : '');
      setEditingGroupId(group ? group.employeeGroupId : null);
      setGroupNameError(null);
      setIsAddingGroup(false);
    };

    const handleUpdateNameClick = (groupId: number) => () => {
      actions.updateOrgEmployeeGroup(groupId, {
        name: newGroupName,
      });
      handleEditToggle()();
    };

    const handleAddEmployeeToggle = (employeeGroupId: number | null) => () => {
      setAddingEmployeeToGroupId(employeeGroupId);
    };

    const handleAddEmployee =
      (groupId: number) => (_e: React.ChangeEvent<{}>, option: SelectOption<number> | null) => {
        if (!option) {
          return;
        }

        const employeeId = option.value;
        const employee = employees.find((e) => e.employeeId === employeeId);

        if (!employee) {
          return;
        }

        const newGroupIds = employee.EmployeeGroups.map((g) => g.employeeGroupId).concat([groupId]);
        actions.updateEmployee(employeeId, { EmployeeGroups: newGroupIds });

        ProductTelemetryController.logEvent('employee added to group', {
          value: employee.employeeId,
          metadata: {
            employeeId: String(employee.employeeId),
            employeeGroupId: String(groupId),
          },
        });
      };

    const employeeOptionsForGroupId = (groupId: number) =>
      employees
        .filter(
          (employee) =>
            employee.isCurrentEmployee &&
            !employee.EmployeeGroups.find((group) => group.employeeGroupId === groupId),
        )
        .map((employee) => ({
          label: employeeName(employee),
          value: employee.employeeId,
        }))
        .sort(sortAlphabetically((option) => option.label.toLowerCase(), SORT_ORDER.ASC));

    const renderAddGroupButton = (label: string, Icon?: React.ReactNode) => {
      if (!hasFullEmployeePermission) {
        return null;
      }

      return (
        <UiButton size="sm" onClick={handleAddToggle} startIcon={Icon}>
          {label}
        </UiButton>
      );
    };

    if (!employees || !employeeGroups) {
      return <LoadingMessage />;
    }

    if (employeeGroups.length === 0) {
      return (
        <>
          <UiBreadcrumbHeader
            pageTitle="Employee Groups"
            customPrimaryAction={renderAddGroupButton('Add Group', <HeaderButtonAddIcon />)}
            backToPage={employeesRoute(orgId)}
          />
          <div className="u-maxWidth-550px u-height-100 u-textAlign-center u-margin-0Auto u-display-flex u-justifyContent-center u-flexDirection-column u-alignItems-center">
            <div className="u-maxWidth-450px u-width-100 u-display-inlineBlock">
              <EmployeeGroupsEmptyState />
            </div>
            <div className="typo-title u-margin-top-2x u-margin-bottom-2x">
              Employee groups are a great way to organize your workforce. Once you&apos;ve created a
              group and added employees to it, you can easily send them curated messages,
              announcements, and surveys.
            </div>
            {!hasFullEmployeePermission && (
              <div className="typo-title">
                Your company doesn&apos;t currently have any groups setup. Talk to your account
                admin or{' '}
                <span
                  role="presentation"
                  className="typo-link"
                  onClick={() => HelpScout.beacon.navigate('/ask/')}
                >
                  contact us{' '}
                </span>
                to get started.
              </div>
            )}
            {renderAddGroupButton('Get Started, Add A Group')}
          </div>
        </>
      );
    }

    const getGroupCard = (group: EmployeeGroup) => (
      <div
        className="card u-margin-bottom-xl"
        style={{ width: cardWidth }}
        key={group.employeeGroupId}
      >
        <div className="u-padding card-head">
          <div className="u-spread">
            {editingGroupId === group.employeeGroupId ? (
              <div className="u-flex-center">
                <TextField
                  autoFocus
                  onKeyDown={handleKeyDown(handleUpdateNameClick(group.employeeGroupId))}
                  onChange={handleGroupNameChange}
                  value={newGroupName}
                  variant="outlined"
                  margin="dense"
                />
                <UiButton
                  className="u-margin-left-sm u-margin-right-sm"
                  size="sm"
                  onClick={handleUpdateNameClick(group.employeeGroupId)}
                  disabled={Boolean(groupNameError)}
                >
                  Update
                </UiButton>
                <UiButton variant="dismiss" size="sm" onClick={handleEditToggle()}>
                  Cancel
                </UiButton>
              </div>
            ) : (
              <div className="typo-h6">{group.name}</div>
            )}
            {hasFullEmployeePermission && !isIntegrationGroup(group) && (
              <div>
                <UiButton
                  variant="tertiary"
                  size="xs"
                  onClick={handleDeleteClick(group.employeeGroupId)}
                  className="u-margin-right"
                >
                  Delete
                </UiButton>
                <UiButton variant="tertiary" onClick={handleEditToggle(group)} size="xs">
                  Edit
                </UiButton>
              </div>
            )}
          </div>
          {editingGroupId === group.employeeGroupId && groupNameError ? (
            <div className="typo-subtitle u-color-red">{groupNameError}</div>
          ) : null}
        </div>
        <div className="card-body u-flex-center u-flexWrap-wrap">
          {employeesForGroupId(employees, group.employeeGroupId).map((employee) => (
            <Chip
              className="u-margin-top-xs u-margin-bottom-xs u-margin-right-sm"
              key={employee.employeeId}
              size="small"
              color="primary"
              label={employeeName(employee)}
              onDelete={
                isIntegrationGroup(group)
                  ? undefined
                  : handleRemoveFromGroup(employee, group.employeeGroupId)
              }
            />
          ))}
          {addingEmployeeToGroupId === group.employeeGroupId && (
            <Autocomplete
              autoHighlight
              className="u-width-350px u-margin-top-xs u-margin-bottom-xs"
              openOnFocus
              onClose={handleAddEmployeeToggle(null)}
              size="small"
              options={employeeOptionsForGroupId(group.employeeGroupId)}
              getOptionLabel={(option) => option.label}
              onChange={handleAddEmployee(group.employeeGroupId)}
              renderInput={(params) => (
                <TextField {...params} autoFocus variant="outlined" fullWidth />
              )}
            />
          )}
          {!addingEmployeeToGroupId && !isIntegrationGroup(group) && (
            <UiButton
              size="xxs"
              variant="tertiary"
              onClick={handleAddEmployeeToggle(group.employeeGroupId)}
            >
              Add Employee
            </UiButton>
          )}
        </div>
      </div>
    );

    const orderedGroups = employeeGroups.sort(
      sortAlphabetically((group) => group.name.toLowerCase(), SORT_ORDER.ASC),
    );
    const nonIntegrationGroups = orderedGroups.filter((group) => !isIntegrationGroup(group));
    const integrationGroups = orderedGroups.filter((group) => isIntegrationGroup(group));
    const cardWidth = isWidthDown('md', width) ? '100%' : 'calc(50% - 10px)';

    return (
      <div>
        <UiBreadcrumbHeader
          pageTitle="Employee Groups"
          customPrimaryAction={renderAddGroupButton('Add Group', <HeaderButtonAddIcon />)}
          backToPage={employeesRoute(orgId)}
        />
        <div className="u-margin-lg">
          <div className="u-display-flex u-justifyContent-spaceBetween u-flexWrap-wrap">
            {nonIntegrationGroups.map(getGroupCard)}
          </div>
          {Boolean(integrationGroups.length) && (
            <div className="card-head">
              <div className="typo-h6">Imported groups</div>
              <div className="typo-subtitle">
                These groups have been imported from an HR/payroll integration. Edit them in your
                HR/payroll system.
              </div>
            </div>
          )}
          <div className="u-display-flex u-justifyContent-spaceBetween u-flexWrap-wrap">
            {integrationGroups.map(getGroupCard)}
          </div>
        </div>
        {isAddingGroup && (
          <UiDialog title="Add New Group" onClose={handleAddToggle} size="sm">
            <UiDialog.Content>
              <div className="u-flex-center">
                <TextField
                  label="Group Name"
                  autoFocus
                  onKeyDown={handleKeyDown(handleCreateGroup)}
                  onChange={handleGroupNameChange}
                  value={newGroupName}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                />
              </div>
              {groupNameError ? (
                <div className="typo-subtitle u-color-red u-margin-bottom">{groupNameError}</div>
              ) : null}
            </UiDialog.Content>
            <UiDialog.Actions>
              <UiButton variant="dismiss" size="sm" onClick={handleAddToggle}>
                Cancel
              </UiButton>
              <UiButton
                className="u-margin-left-sm u-margin-right-sm"
                size="sm"
                onClick={handleCreateGroup}
                disabled={Boolean(groupNameError)}
              >
                Create
              </UiButton>
            </UiDialog.Actions>
          </UiDialog>
        )}
      </div>
    );
  }),
);
